// ============================================================================
// Overview-sidan (2026-05-24)
// Pivot-vy över alla 3 bolag (LS/OD/LSI), månad för månad. Tre layouter A/B/C
// + metric-tabs (Vinst/Förlust, Momsbalans, Intäkter, Kostnader).
// Klick i cell → MonthDetailDialog för det bolag × månad.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { signOut } from '../firebase'
import { useYear } from '../auth/YearContext'
import { COMPANIES, type Company } from '../data/companies'
import {
  subscribeReceipts,
  subscribeYear,
  aggregateReceipts,
  type CloudReceipt,
  type CloudYearDoc,
} from '../lib/cloudStore'
import type { MonthlyRow } from '../data/store'
import { emptyMonths } from '../data/store'
import { MonthDetailDialog } from '../components/MonthDetailDialog'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const SHORT = { occdec: 'OD', 'linesp-ab': 'LS', 'linesp-fo': 'LSI' } as Record<string, string>
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })
const fmtSigned = (n: number) => (n > 0 ? '+' : '') + fmt(n)

type Metric = 'pl' | 'moms' | 'income' | 'expense'
type LayoutMode = 'A' | 'B' | 'C'

const METRIC_LABELS: Record<Metric, string> = {
  pl: 'Vinst / förlust',
  moms: 'Momsbalans (att betala)',
  income: 'Intäkter',
  expense: 'Kostnader',
}

function metricValue(m: MonthlyRow, metric: Metric): number {
  switch (metric) {
    case 'pl': return m.income - m.expense
    case 'moms': return m.vatOut - m.vatIn  // positivt = betala till SKV
    case 'income': return m.income
    case 'expense': return m.expense
  }
}

/** För moms+pl: positivt-bra eller positivt-dåligt? Påverkar färgkodningen. */
function isPositiveGood(metric: Metric): boolean {
  // Vinst, intäkter → positivt grön
  // Kostnader → positivt rött (mer kostnader = sämre)
  // Moms-att-betala → positivt rött (mer att betala = sämre)
  return metric === 'pl' || metric === 'income'
}

function cellColor(value: number, magnitude: number, metric: Metric): { color: string; bg: string } {
  if (value === 0 || magnitude === 0) return { color: 'var(--text-muted)', bg: 'transparent' }
  const positiveGood = isPositiveGood(metric)
  const isGood = positiveGood ? value > 0 : value < 0
  const intensity = Math.min(1, Math.abs(value) / magnitude)
  const opacity = 0.05 + intensity * 0.18  // 0.05..0.23
  return {
    color: isGood ? 'var(--success)' : 'var(--danger)',
    bg: isGood ? `rgba(16,185,129,${opacity.toFixed(2)})` : `rgba(239,68,68,${opacity.toFixed(2)})`,
  }
}

interface CompanyAgg {
  company: Company
  months: MonthlyRow[]  // length 12
  receipts: CloudReceipt[]
  skattekonto: number   // saldo från year-doc
}

interface OverviewProps {
  onClose: () => void
}

export function Overview(props: OverviewProps) {
  const { onClose } = props
  const { user } = useAuth()
  const { year, setYear, availableYears } = useYear()

  const [layout, setLayout] = useState<LayoutMode>('A')
  const [metric, setMetric] = useState<Metric>('pl')
  const [detail, setDetail] = useState<{ companyId: string; month: number } | null>(null)

  // Subscribe receipts + year-docs (för skattekonto) för alla 3 bolag
  const [receiptsByCompany, setReceiptsByCompany] = useState<Record<string, CloudReceipt[]>>({})
  const [yearByCompany, setYearByCompany] = useState<Record<string, CloudYearDoc>>({})

  useEffect(() => {
    const unsubs: (() => void)[] = []
    COMPANIES.forEach((c) => {
      try {
        unsubs.push(subscribeReceipts(c.id, year, (list) => {
          setReceiptsByCompany((prev) => ({ ...prev, [c.id]: list }))
        }))
        unsubs.push(subscribeYear(c.id, year, (yd) => {
          setYearByCompany((prev) => ({ ...prev, [c.id]: yd }))
        }))
      } catch { /* not authed yet */ }
    })
    return () => unsubs.forEach((u) => { try { u() } catch { /* ignore */ } })
  }, [year])

  const aggs: CompanyAgg[] = useMemo(() => {
    return COMPANIES.map((c) => {
      const recList = receiptsByCompany[c.id] || []
      const recMonths = aggregateReceipts(recList)
      const sieMonths = yearByCompany[c.id]?.months
      // Merge SIE + receipts: SIE tar prio om det finns vatIn/vatOut
      const merged = recMonths.map((rec, i) => {
        const sie = sieMonths?.[i]
        if (!sie) return rec
        const sieHasVat = sie.vatIn !== 0 || sie.vatOut !== 0
        const sieHasPL  = sie.income !== 0 || sie.expense !== 0
        return {
          month: i,
          vatIn:   sieHasVat ? sie.vatIn  : rec.vatIn,
          vatOut:  sieHasVat ? sie.vatOut : rec.vatOut,
          income:  sieHasPL  ? sie.income  : rec.income,
          expense: sieHasPL  ? sie.expense : rec.expense,
        }
      })
      return {
        company: c,
        receipts: recList,
        months: merged.length === 12 ? merged : emptyMonths(),
        skattekonto: yearByCompany[c.id]?.skattekontoSaldo ?? 0,
      }
    })
  }, [receiptsByCompany, yearByCompany])

  const now = new Date()
  const uptoMonth = year === now.getFullYear() ? now.getMonth() : 11

  // Beräkna max magnitude för heatmap-skalning (per metric, alla bolag + månader)
  const magnitude = useMemo(() => {
    let max = 0
    for (const a of aggs) {
      for (let i = 0; i <= uptoMonth; i++) {
        max = Math.max(max, Math.abs(metricValue(a.months[i], metric)))
      }
    }
    return max
  }, [aggs, metric, uptoMonth])

  const detailCompany = detail ? COMPANIES.find((c) => c.id === detail.companyId) : null
  const detailReceipts = detail ? (receiptsByCompany[detail.companyId] || []) : []

  return (
    <div className="shell shell--wide">
      <header className="topbar">
        <div className="topbar__brand">Översikt</div>
        <div className="topbar__right">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{
              background: 'var(--bg-card)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '0.4rem 0.7rem', fontSize: '0.95rem',
            }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{'År ' + y}</option>
            ))}
          </select>
          <button className="logout-btn" onClick={onClose}>{'← Tillbaka'}</button>
          {user && (
            <>
              <span className="user-chip">
                {user.photoURL ? <img src={user.photoURL} alt="" /> : <span>{'👤'}</span>}
                <span>{user.email}</span>
              </span>
              <button className="logout-btn" onClick={() => signOut()}>Logga ut</button>
            </>
          )}
        </div>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {'Visa:'}
        </div>
        {(['pl', 'moms', 'income', 'expense'] as Metric[]).map((m) => (
          <button
            key={m}
            onClick={() => setMetric(m)}
            className="tab-btn"
            style={{
              background: metric === m ? 'var(--primary)' : 'transparent',
              color: metric === m ? '#fff' : 'var(--text)',
              border: '1px solid ' + (metric === m ? 'var(--primary)' : 'var(--border)'),
              borderRadius: 6, padding: '0.4rem 0.8rem', fontSize: '0.85rem',
              cursor: 'pointer', fontWeight: metric === m ? 600 : 400,
            }}
          >
            {METRIC_LABELS[m]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {'Layout:'}
        </div>
        {(['A', 'B', 'C'] as LayoutMode[]).map((L) => (
          <button
            key={L}
            onClick={() => setLayout(L)}
            style={{
              background: layout === L ? 'var(--bg-card-alt)' : 'transparent',
              color: 'var(--text)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.8rem',
              cursor: 'pointer', fontWeight: layout === L ? 700 : 400,
              minWidth: 36,
            }}
            title={L === 'A' ? 'Pivot-tabell' : L === 'B' ? 'Kort per månad' : 'Tabell + sparkline'}
          >
            {L}
          </button>
        ))}
      </div>

      {layout === 'A' && <LayoutA aggs={aggs} uptoMonth={uptoMonth} metric={metric} magnitude={magnitude} year={year} onCell={(cid, m) => setDetail({ companyId: cid, month: m })} />}
      {layout === 'B' && <LayoutB aggs={aggs} uptoMonth={uptoMonth} metric={metric} magnitude={magnitude} onCell={(cid, m) => setDetail({ companyId: cid, month: m })} />}
      {layout === 'C' && <LayoutC aggs={aggs} uptoMonth={uptoMonth} metric={metric} magnitude={magnitude} year={year} onCell={(cid, m) => setDetail({ companyId: cid, month: m })} />}

      {detail && detailCompany && (
        <MonthDetailDialog
          month={detail.month}
          year={year}
          companyName={detailCompany.name}
          receipts={detailReceipts}
          onClose={() => setDetail(null)}
        />
      )}

      <footer style={{ marginTop: '2rem', fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        {COMPANIES.length + ' bolag · År ' + year + ' · ' + METRIC_LABELS[metric] + ' · Layout ' + layout}
      </footer>
    </div>
  )
}

// ============================================================================
// LAYOUT A — Pivot-tabell med heatmap
// ============================================================================
interface LayoutProps {
  aggs: CompanyAgg[]
  uptoMonth: number
  metric: Metric
  magnitude: number
  year: number
  onCell: (companyId: string, month: number) => void
}

function LayoutA(props: Omit<LayoutProps, 'year'> & { year: number }) {
  const { aggs, uptoMonth, metric, magnitude, onCell, year } = props
  const visible = MONTHS.slice(0, uptoMonth + 1)

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
    fontVariantNumeric: 'tabular-nums',
  }
  const th: React.CSSProperties = {
    fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em',
    color: 'var(--text-muted)', padding: '0.5rem 0.8rem', textAlign: 'right',
    borderBottom: '1px solid var(--border)', fontWeight: 600,
  }
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' }
  const td: React.CSSProperties = {
    padding: '0.45rem 0.8rem', textAlign: 'right', cursor: 'pointer',
    borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.05))',
  }
  const tdMonth: React.CSSProperties = { ...td, textAlign: 'left', color: 'var(--text)', fontWeight: 500, cursor: 'default' }

  // Beräkna kolumn-totaler (per bolag, hela året)
  const colTotals = aggs.map((a) =>
    a.months.slice(0, uptoMonth + 1).reduce((s, m) => s + metricValue(m, metric), 0)
  )
  const grandTotal = colTotals.reduce((s, v) => s + v, 0)

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thLeft}>{''}</th>
            {aggs.map((a) => (
              <th key={a.company.id} style={th} title={a.company.name}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.85rem' }}>{SHORT[a.company.id] || a.company.id}</span>
                  <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{a.company.name}</span>
                </div>
              </th>
            ))}
            <th style={{ ...th, borderLeft: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.85rem' }}>{'TOTALT'}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((mName, i) => {
            const rowVals = aggs.map((a) => metricValue(a.months[i], metric))
            const rowTotal = rowVals.reduce((s, v) => s + v, 0)
            return (
              <tr key={i}>
                <td style={tdMonth}>{mName}</td>
                {aggs.map((a, ci) => {
                  const v = rowVals[ci]
                  const c = cellColor(v, magnitude, metric)
                  return (
                    <td
                      key={a.company.id}
                      style={{ ...td, background: c.bg, color: v === 0 ? 'var(--text-muted)' : c.color }}
                      onClick={() => onCell(a.company.id, i)}
                      title={a.company.name + ' · ' + mName + ' — klick för topp 10'}
                    >
                      {v === 0 ? '—' : fmtSigned(v)}
                    </td>
                  )
                })}
                <td style={{ ...td, fontWeight: 700, borderLeft: '1px solid var(--border)', cursor: 'default' }}>
                  {rowTotal === 0 ? '—' : fmtSigned(rowTotal)}
                </td>
              </tr>
            )
          })}
          {/* Total row */}
          <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
            <td style={{ ...tdMonth, fontWeight: 700, borderTop: '2px solid var(--border)', cursor: 'default' }}>
              {'ÅR ' + year}
            </td>
            {colTotals.map((v, ci) => (
              <td key={aggs[ci].company.id} style={{
                ...td, fontWeight: 700, borderTop: '2px solid var(--border)', cursor: 'default',
                color: v === 0 ? 'var(--text-muted)' : (isPositiveGood(metric) ? (v > 0 ? 'var(--success)' : 'var(--danger)') : (v > 0 ? 'var(--danger)' : 'var(--success)')),
              }}>
                {v === 0 ? '—' : fmtSigned(v)}
              </td>
            ))}
            <td style={{
              ...td, fontWeight: 700, borderTop: '2px solid var(--border)', borderLeft: '1px solid var(--border)',
              cursor: 'default', fontSize: '1rem',
              color: grandTotal === 0 ? 'var(--text-muted)' : (isPositiveGood(metric) ? (grandTotal > 0 ? 'var(--success)' : 'var(--danger)') : (grandTotal > 0 ? 'var(--danger)' : 'var(--success)')),
            }}>
              {grandTotal === 0 ? '—' : fmtSigned(grandTotal)}
            </td>
          </tr>
          {/* Bonus rows för Momsbalans: skattekonto + netto behov */}
          {metric === 'moms' && <MomsExtraRows aggs={aggs} colTotals={colTotals} grandTotal={grandTotal} tdMonth={tdMonth} td={td} />}
        </tbody>
      </table>
    </div>
  )
}

// Hjälpkomponent — skattekonto-rad + netto-rad. Visas bara för Momsbalans.
interface MomsExtraProps {
  aggs: CompanyAgg[]
  colTotals: number[]   // moms att betala per bolag (vatOut - vatIn över året)
  grandTotal: number
  tdMonth: React.CSSProperties
  td: React.CSSProperties
}

function MomsExtraRows(props: MomsExtraProps) {
  const { aggs, colTotals, grandTotal, tdMonth, td } = props
  const sumSkattekonto = aggs.reduce((s, a) => s + a.skattekonto, 0)
  // Netto = moms-att-betala - skattekonto. Positivt = behov att föra över. Negativt = överskott.
  const nettos = colTotals.map((v, i) => v - aggs[i].skattekonto)
  const nettoTotal = grandTotal - sumSkattekonto
  return (
    <>
      <tr>
        <td style={{ ...tdMonth, cursor: 'default', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {'Skattekonto'}
        </td>
        {aggs.map((a) => (
          <td key={a.company.id} style={{
            ...td, cursor: 'default', color: 'var(--text-muted)', fontSize: '0.85rem',
          }}>
            {a.skattekonto === 0 ? '—' : fmt(a.skattekonto)}
          </td>
        ))}
        <td style={{
          ...td, cursor: 'default', color: 'var(--text-muted)', fontSize: '0.85rem',
          borderLeft: '1px solid var(--border)',
        }}>
          {sumSkattekonto === 0 ? '—' : fmt(sumSkattekonto)}
        </td>
      </tr>
      <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
        <td style={{ ...tdMonth, cursor: 'default', fontWeight: 700, borderTop: '1px dashed var(--border)' }}>
          {'VAT NETTO'}
          <div style={{ fontSize: '0.65rem', fontWeight: 400, color: 'var(--text-muted)' }}>
            {'(+) behov  (−) överskott'}
          </div>
        </td>
        {nettos.map((v, ci) => (
          <td key={aggs[ci].company.id} style={{
            ...td, cursor: 'default', fontWeight: 700, borderTop: '1px dashed var(--border)',
            color: v === 0 ? 'var(--text-muted)' : (v > 0 ? 'var(--danger)' : 'var(--success)'),
          }}>
            {v === 0 ? '—' : fmtSigned(v)}
          </td>
        ))}
        <td style={{
          ...td, cursor: 'default', fontWeight: 700, fontSize: '1rem',
          borderTop: '1px dashed var(--border)', borderLeft: '1px solid var(--border)',
          color: nettoTotal === 0 ? 'var(--text-muted)' : (nettoTotal > 0 ? 'var(--danger)' : 'var(--success)'),
        }}>
          {nettoTotal === 0 ? '—' : fmtSigned(nettoTotal)}
        </td>
      </tr>
    </>
  )
}

// ============================================================================
// LAYOUT B — Small multiples (ett kort per månad)
// ============================================================================
function LayoutB(props: Omit<LayoutProps, 'year'>) {
  const { aggs, uptoMonth, metric, magnitude, onCell } = props
  const visible = MONTHS.slice(0, uptoMonth + 1)
  // YTD-totaler för Moms-rutan (visas bara när metric=moms)
  const colTotals = aggs.map((a) =>
    a.months.slice(0, uptoMonth + 1).reduce((s, m) => s + metricValue(m, metric), 0)
  )
  const grandTotal = colTotals.reduce((s, v) => s + v, 0)
  const sumSkattekonto = aggs.reduce((s, a) => s + a.skattekonto, 0)
  const nettoTotal = grandTotal - sumSkattekonto

  return (
    <>
      {metric === 'moms' && (
        <div className="card" style={{ marginBottom: '0.8rem', padding: '0.9rem 1.2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.8rem' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{'Att betala SKV'}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: grandTotal > 0 ? 'var(--danger)' : grandTotal < 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                {grandTotal === 0 ? '—' : fmtSigned(grandTotal) + ' kr'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{'Skattekonto-saldo'}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' }}>
                {sumSkattekonto === 0 ? '—' : fmt(sumSkattekonto) + ' kr'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                {nettoTotal > 0 ? 'Behov att föra över' : 'Överskott efter SKV'}
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: nettoTotal > 0 ? 'var(--danger)' : 'var(--success)' }}>
                {nettoTotal === 0 ? '—' : fmt(Math.abs(nettoTotal)) + ' kr'}
              </div>
            </div>
          </div>
        </div>
      )}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '0.8rem',
    }}>
      {visible.map((mName, i) => {
        const rowVals = aggs.map((a) => metricValue(a.months[i], metric))
        const total = rowVals.reduce((s, v) => s + v, 0)
        return (
          <div key={i} className="card" style={{ padding: '0.9rem 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>{mName}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{METRIC_LABELS[metric]}</span>
            </div>
            {aggs.map((a, ci) => {
              const v = rowVals[ci]
              const c = cellColor(v, magnitude, metric)
              return (
                <div
                  key={a.company.id}
                  onClick={() => onCell(a.company.id, i)}
                  style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '0.35rem 0.5rem', marginBottom: 2, borderRadius: 4,
                    cursor: 'pointer', fontVariantNumeric: 'tabular-nums',
                    background: c.bg, fontSize: '0.88rem',
                  }}
                  title={a.company.name + ' · ' + mName + ' — klick för topp 10'}
                >
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 600 }}>{SHORT[a.company.id]}</span>
                  <span style={{ color: v === 0 ? 'var(--text-muted)' : c.color, fontWeight: 600 }}>
                    {v === 0 ? '—' : fmtSigned(v)}
                  </span>
                </div>
              )
            })}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6,
              fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            }}>
              <span style={{ color: 'var(--text-muted)' }}>{'Totalt'}</span>
              <span style={{
                color: total === 0 ? 'var(--text-muted)' : (isPositiveGood(metric) ? (total > 0 ? 'var(--success)' : 'var(--danger)') : (total > 0 ? 'var(--danger)' : 'var(--success)')),
              }}>
                {total === 0 ? '—' : fmtSigned(total)}
              </span>
            </div>
          </div>
        )
      })}
    </div>
    </>
  )
}

// ============================================================================
// LAYOUT C — Pivot-tabell (som A) + sparkline ovanför per bolag
// ============================================================================
function LayoutC(props: LayoutProps) {
  const { aggs, uptoMonth, metric, magnitude, onCell, year } = props
  const visible = MONTHS.slice(0, uptoMonth + 1)

  // Sparkline-data per bolag
  function sparkline(months: MonthlyRow[]) {
    const W = 100, H = 28, pad = 2
    const vals = months.slice(0, uptoMonth + 1).map((m) => metricValue(m, metric))
    const maxAbs = Math.max(1, ...vals.map(Math.abs))
    const positiveGood = isPositiveGood(metric)
    const pts = vals.map((v, i) => {
      const x = vals.length === 1 ? W / 2 : pad + (i / (vals.length - 1)) * (W - pad * 2)
      const y = H / 2 - (v / maxAbs) * (H / 2 - pad)
      return [x, y] as [number, number]
    })
    const dPath = 'M' + pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' L')
    return (
      <svg viewBox={'0 0 ' + W + ' ' + H} width={W} height={H} preserveAspectRatio="none">
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 2" />
        <path d={dPath} fill="none" stroke="var(--text-muted)" strokeWidth="1.2" opacity="0.6" />
        {pts.map((p, i) => {
          const v = vals[i]
          const isGood = positiveGood ? v > 0 : v < 0
          return (
            <circle
              key={i}
              cx={p[0]}
              cy={p[1]}
              r="1.5"
              fill={v === 0 ? 'var(--text-muted)' : (isGood ? '#10b981' : '#ef4444')}
            />
          )
        })}
      </svg>
    )
  }

  const tableStyle: React.CSSProperties = {
    width: '100%', borderCollapse: 'collapse',
    fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums',
  }
  const th: React.CSSProperties = {
    fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em',
    color: 'var(--text-muted)', padding: '0.5rem 0.8rem', textAlign: 'right',
    borderBottom: '1px solid var(--border)', fontWeight: 600,
  }
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' }
  const td: React.CSSProperties = {
    padding: '0.45rem 0.8rem', textAlign: 'right', cursor: 'pointer',
    borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.05))',
  }
  const tdMonth: React.CSSProperties = { ...td, textAlign: 'left', color: 'var(--text)', fontWeight: 500, cursor: 'default' }

  const colTotals = aggs.map((a) =>
    a.months.slice(0, uptoMonth + 1).reduce((s, m) => s + metricValue(m, metric), 0)
  )
  const grandTotal = colTotals.reduce((s, v) => s + v, 0)

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thLeft}>{''}</th>
            {aggs.map((a) => (
              <th key={a.company.id} style={th} title={a.company.name}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.85rem' }}>{SHORT[a.company.id]}</span>
                  <span style={{ fontSize: '0.6rem', opacity: 0.65 }}>{a.company.name}</span>
                  {sparkline(a.months)}
                </div>
              </th>
            ))}
            <th style={{ ...th, borderLeft: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.85rem' }}>{'TOTALT'}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((mName, i) => {
            const rowVals = aggs.map((a) => metricValue(a.months[i], metric))
            const rowTotal = rowVals.reduce((s, v) => s + v, 0)
            return (
              <tr key={i}>
                <td style={tdMonth}>{mName}</td>
                {aggs.map((a, ci) => {
                  const v = rowVals[ci]
                  const c = cellColor(v, magnitude, metric)
                  return (
                    <td
                      key={a.company.id}
                      style={{ ...td, background: c.bg, color: v === 0 ? 'var(--text-muted)' : c.color }}
                      onClick={() => onCell(a.company.id, i)}
                      title={a.company.name + ' · ' + mName + ' — klick för topp 10'}
                    >
                      {v === 0 ? '—' : fmtSigned(v)}
                    </td>
                  )
                })}
                <td style={{ ...td, fontWeight: 700, borderLeft: '1px solid var(--border)', cursor: 'default' }}>
                  {rowTotal === 0 ? '—' : fmtSigned(rowTotal)}
                </td>
              </tr>
            )
          })}
          <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
            <td style={{ ...tdMonth, fontWeight: 700, borderTop: '2px solid var(--border)', cursor: 'default' }}>
              {'ÅR ' + year}
            </td>
            {colTotals.map((v, ci) => (
              <td key={aggs[ci].company.id} style={{
                ...td, fontWeight: 700, borderTop: '2px solid var(--border)', cursor: 'default',
                color: v === 0 ? 'var(--text-muted)' : (isPositiveGood(metric) ? (v > 0 ? 'var(--success)' : 'var(--danger)') : (v > 0 ? 'var(--danger)' : 'var(--success)')),
              }}>
                {v === 0 ? '—' : fmtSigned(v)}
              </td>
            ))}
            <td style={{
              ...td, fontWeight: 700, borderTop: '2px solid var(--border)', borderLeft: '1px solid var(--border)',
              cursor: 'default', fontSize: '1rem',
              color: grandTotal === 0 ? 'var(--text-muted)' : (isPositiveGood(metric) ? (grandTotal > 0 ? 'var(--success)' : 'var(--danger)') : (grandTotal > 0 ? 'var(--danger)' : 'var(--success)')),
            }}>
              {grandTotal === 0 ? '—' : fmtSigned(grandTotal)}
            </td>
          </tr>
          {metric === 'moms' && <MomsExtraRows aggs={aggs} colTotals={colTotals} grandTotal={grandTotal} tdMonth={tdMonth} td={td} />}
        </tbody>
      </table>
    </div>
  )
}
